registerComponent("sections/investimentos", function (component) {
/**
 * INVESTIMENTOS — o letreiro das categorias no celular (ADR 0042).
 *
 * Abaixo de 768 as oito categorias viram chips em duas fileiras que ANDAM: a de
 * cima para a esquerda, a de baixo para a direita (frame 10680:33292, chips
 * 33724–33745 deslocados e cortados nas bordas; o movimento é pedido do cliente
 * de 04/09). Quem anima é o CSS (`@keyframes invest-letreiro` em
 * _investimentos.less, sobre o `translate` do trilho); este script faz o que o
 * CSS não consegue:
 *
 *   · COMPLETAR o trilho para o laço fechar sem emenda. O laço é "andar meio
 *     trilho e recomeçar", então o trilho precisa ser DUAS metades idênticas, e
 *     cada metade mais larga que a janela — senão sobra vazio à vista. Cada
 *     metade é o conjunto dos quatro chips repetido `k` vezes (`k = ⌈janela /
 *     conjunto⌉`); as cópias são `aria-hidden` (o conteúdo é o mesmo);
 *
 *   · DAR O TEMPO em `--letreiro-dur`, a partir da largura medida, para as duas
 *     fileiras andarem na MESMA velocidade (px/s) — com duração fixa a fileira
 *     mais larga correria mais;
 *
 *   · LIGAR só onde faz sentido: na faixa de celular (`Asa.celular`) e com
 *     movimento permitido. Fora disso — sem JS, no desktop, ou com
 *     `prefers-reduced-motion` — os trilhos são as fileiras roláveis de sempre
 *     e nenhuma cópia existe;
 *
 *   · PARAR quando não há quem veja (fora da janela, `IntersectionObserver`),
 *     enquanto o dedo ou o ponteiro segura uma fileira e, desde 08/09 à noite
 *     (ADR 0056), por um TOQUE que trava a pausa até o toque seguinte, na
 *     fileira ou fora dela (`.invest--pausado`) — o controle de pausa da WCAG
 *     2.2.2. A classe vai na RAIZ, e não na fileira tocada, para as duas
 *     pausarem juntas (ADR 0047): uma parada e a outra andando é o que o
 *     cliente não quer.
 *
 * @param {HTMLElement} component - elemento raiz do componente (a <section class="invest">)
 * @param {Object} params
 */

const VELOCIDADE = 36;   /* px por segundo — lento o bastante para ler o chip que passa */

const trilhos = Array.from(component.querySelectorAll('.invest__trilho'));

if (trilhos.length && 'IntersectionObserver' in window) {
    /* Os chips originais de cada trilho — o conjunto que se repete. */
    const originais = new Map(trilhos.map((t) => [t, Array.from(t.children)]));

    let ligado = false;
    let travado = false;     /* a trava de pausa do toque — ver "O CONTROLE DE PAUSA" */

    const desmontar = () => {
        for (const trilho of trilhos) {
            for (const copia of trilho.querySelectorAll('[data-copia]')) copia.remove();
            trilho.style.removeProperty('--letreiro-dur');
        }

        component.classList.remove('invest--letreiro', 'invest--pausado');
        travado = false;
        ligado = false;
    };

    /* Monta (ou remonta) as cópias de um trilho para a janela atual. Mede o
       conjunto ANTES de copiar, com o trilho já em `max-content` (o CSS do
       estado ligado). */
    const montar = (trilho) => {
        for (const copia of trilho.querySelectorAll('[data-copia]')) copia.remove();

        const janela = trilho.parentElement.clientWidth;
        const vao = parseFloat(getComputedStyle(trilho).columnGap) || 0;
        const conjunto = trilho.scrollWidth + vao;      /* os quatro chips mais o vão até o próximo */

        if (!janela || !conjunto) return;

        const k = Math.max(1, Math.ceil(janela / conjunto));
        const copias = 2 * k - 1;                        /* duas metades de k conjuntos, uma já está lá */
        /* Os chips do letreiro NÃO fazem a entrada da pilha (ADR 0046): o CSS
           já os mostra sem `.is-in` sob `.invest--letreiro`, e a marca vai nos
           originais ANTES de copiar — as cópias nascem dela por `cloneNode`, e
           quando o letreiro desliga (giro para 769+) os chips já estão no
           estado final em vez de voltar ao "antes" e esperar o observador. */
        for (const chip of originais.get(trilho)) chip.classList.add('is-in');

        const fragmento = document.createDocumentFragment();

        for (let i = 0; i < copias; i += 1) {
            for (const chip of originais.get(trilho)) {
                const copia = chip.cloneNode(true);

                copia.setAttribute('aria-hidden', 'true');
                copia.dataset.copia = '';
                fragmento.appendChild(copia);
            }
        }

        trilho.appendChild(fragmento);

        /* Meio trilho por volta: k conjuntos, na velocidade combinada. */
        trilho.style.setProperty('--letreiro-dur', `${((k * conjunto) / VELOCIDADE).toFixed(2)}s`);
    };

    const montarTodos = () => {
        component.classList.add('invest--letreiro');
        for (const trilho of trilhos) montar(trilho);
        ligado = true;
    };

    /* Fora da janela, nada anda. */
    const visivel = new IntersectionObserver((entradas) => {
        for (const entrada of entradas) {
            component.classList.toggle('invest--fora', !entrada.isIntersecting);
        }
    }, { rootMargin: '10% 0px' });

    visivel.observe(component);

    /* ── O CONTROLE DE PAUSA (ADR 0047, revisto em 08/09 à noite: ADR 0056) ──
       Um TOQUE numa fileira pausa as duas e deixa pausado; o toque seguinte —
       na fileira ou fora dela — retoma. Segurar continua pausando enquanto o
       dedo está pousado. A classe fica na raiz: qualquer fileira pausa ambas.

       Era "segurar pausa, soltar retoma", e no celular o letreiro ficava
       parado depois do tap até um toque FORA dos chips: não era o JS — era o
       `:hover` do CSS, que no toque fica preso no último elemento tocado até o
       dedo pousar em outro lugar (o "sticky hover"). O hover passou a valer só
       para quem tem mouse (`@media (hover: hover)`, _investimentos.less) e o
       toque ganhou a trava explícita, que é o que o cliente pediu: tocar de
       novo em cima dos chips volta a andar.

       `travado` é a trava do toque; `travadoAoPousar` guarda o estado em que
       ela estava quando o dedo pousou, para o `pointerup` decidir se alterna.
       Um deslize que vira rolagem cancela o ponteiro (`pointercancel`) e nada
       muda — só se retoma se não havia trava. */
    const pausar = () => component.classList.add('invest--pausado');
    const retomar = () => component.classList.remove('invest--pausado');

    let travadoAoPousar = false;

    const acertar = () => (travado ? pausar() : retomar());

    for (const trilho of trilhos) {
        const janela = trilho.parentElement;

        janela.addEventListener('pointerdown', () => {
            travadoAoPousar = travado;
            pausar();
        }, { passive: true });

        janela.addEventListener('pointerup', () => {
            travado = !travadoAoPousar;
            acertar();
        }, { passive: true });

        janela.addEventListener('pointercancel', acertar, { passive: true });
        janela.addEventListener('pointerleave', acertar, { passive: true });
    }

    /* Tocar (ou clicar) em qualquer lugar fora das fileiras solta a trava. No
       `pointerdown` do documento, e não no `click`: um deslize para rolar a
       página fora dos chips já basta para o letreiro voltar. */
    document.addEventListener('pointerdown', (event) => {
        if (!travado) return;
        if (event.target instanceof Element && event.target.closest('.invest__col')) return;

        travado = false;
        retomar();
    }, { passive: true });

    const ligar = (ativo) => {
        if (ativo && Asa.movimentoPermitido()) montarTodos();
        else desmontar();
    };

    ligar(Asa.celular.matches);
    Asa.celular.addEventListener('change', (event) => ligar(event.matches));

    /* A janela mudou de largura (giro do aparelho): o `k` pode mudar. Um quadro
       por rajada, e só com o letreiro ligado. */
    let agendado = false;

    window.addEventListener('resize', () => {
        if (!ligado || agendado) return;

        agendado = true;

        requestAnimationFrame(() => {
            agendado = false;
            if (ligado) for (const trilho of trilhos) montar(trilho);
        });
    }, { passive: true });
}

});
